import type { ProductSlimResponseDTO } from '@shared/DTO';
import { createDomElementHandler as el } from '@/scripts/utils/dom.utils';
import { getProductRating } from '@/scripts/utils/productRating.utils';
import { createStandaloneProductCardViewModel } from '../ab-products/variants/productListingItem';
import type { ProductCardViewModel } from '../ab-products/variants/purchaseCta.types';
import { PurchaseCtaView } from '../purchase-cta/purchaseCta.view';
import { resolveProductContent, ProductContentInputs, ProductAspect } from '../product-content/productContent.model';
import { createProductSpecsLink } from '../product-content/productSpecsLink';
import './abProductSummary.style.scss';

export class AbProductSummaryElement extends HTMLElement {
  #model?: ProductCardViewModel;
  #content: ProductContentInputs = {};
  #cta?: PurchaseCtaView;

  static get observedAttributes() { return ['heading']; }
  attributeChangedCallback() { this.#render(); }
  connectedCallback() { this.#cta?.connect(); }
  disconnectedCallback() { this.#cta?.disconnect(); }

  get product(): ProductSlimResponseDTO | undefined { return this.#model?.product; }
  set product(value: ProductSlimResponseDTO | undefined) { this.cardModel = value ? createStandaloneProductCardViewModel(value) : undefined; }
  get cardModel(): ProductCardViewModel | undefined { return this.#model; }
  set cardModel(value: ProductCardViewModel | undefined) { this.#model = value; this.#render(); }

  get heading() { return this.getAttribute('heading') ?? 'In sintesi'; }
  set heading(value: string) { this.setAttribute('heading', value); }
  get pros() { return this.#content.pros; }
  set pros(value: readonly string[] | null | undefined) { this.#content.pros = value; this.#render(); }
  get cons() { return this.#content.cons; }
  set cons(value: readonly string[] | null | undefined) { this.#content.cons = value; this.#render(); }
  get aspects() { return this.#content.aspects; }
  set aspects(value: readonly ProductAspect[] | null | undefined) { this.#content.aspects = value; this.#render(); }

  #render() {
    this.#cta?.disconnect(); this.#cta = undefined;
    if (!this.#model) { this.replaceChildren(); return; }
    const { product, purchaseCta, purchaseScope } = this.#model;
    const content = resolveProductContent(product, this.#content);
    const root = el('div', 'ab-product-summary');
    if (this.heading) { const heading = el('h3'); heading.textContent = this.heading; root.append(heading); }
    const header = el('div', 'ab-product-summary__header');
    const intro = el('div', 'ab-product-summary__intro');
    const rating = getProductRating(product);
    if (rating) {
      const overall = el('div', 'ab-product-summary__overall');
      const stars = el('ab-stars', [], { rating: String(rating.ratingAvg) });
      const text = el('span');
      text.textContent = `${rating.ratingAvg.toLocaleString('it-IT')}/5`;
      overall.append(stars, text); intro.append(overall);
    }
    if (intro.childElementCount) header.append(intro);
    const notes = el('div', 'ab-product-summary__pros-cons');
    this.#list(notes, 'pros', content.pros); this.#list(notes, 'cons', content.cons);
    if (notes.childElementCount) header.append(notes);
    if (header.childElementCount) root.append(header);
    if (content.aspects?.length) {
      const aspects = el('div', 'ab-product-summary__aspects');
      content.aspects.forEach(({ label, score }) => {
        const row = el('div', 'ab-product-summary__aspect');
        const text = el('span'); text.textContent = `${label} · ${score}/100`;
        // Labels remain plain text, outside the progress component's HTML label renderer.
        const bar = el('ab-progress-bar', [], { value: String(score), role: 'meter',
          'aria-label': label, 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(score) });
        row.append(text, bar); aspects.append(row);
      });
      root.append(aspects);
    }
    this.#cta = new PurchaseCtaView(purchaseCta, { unavailableLabel: 'Non disponibile' });
    const actions = el('div', 'ab-product-summary__actions');
    actions.append(createProductSpecsLink(product._id, purchaseScope), this.#cta.element);
    root.append(actions); this.replaceChildren(root);
    if (this.isConnected) this.#cta.connect();
  }

  #list(parent: HTMLElement, kind: 'pros' | 'cons', items?: readonly string[]) {
    if (!items?.length) return;
    const list = el('ul', ['ab-product-recap-listing__pros-and-cons-list', `ab-product-recap-listing__pros-and-cons-list--${kind}`],
      { 'aria-label': kind === 'pros' ? 'Pro' : 'Contro' });
    items.forEach((item) => {
      const li = el('li', 'ab-product-recap-listing__pros-and-cons-item');
      li.textContent = item; list.append(li);
    });
    parent.append(list);
  }
}

export function defineAbProductSummaryElement() {
  if (!customElements.get('ab-product-summary')) customElements.define('ab-product-summary', AbProductSummaryElement);
}
